import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Chip,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip as MuiTooltip,
    Button,
} from '@mui/material';
import { useGlobalFilters } from '../contexts/FilterContext';
import { SNR } from '../config/api';
import { authFetch } from '../services/authFetch';
import { Refresh as RefreshIcon } from '@mui/icons-material';

// Matches /api/snr/alert-insights response shape
interface AppRow {
    app_id: string;
    app_name: string;
    portfolio_name: string;
    platform_name: string;
    mvc_group: string | null;
    app_status: string | null;
    bca_status: string | null;
    ops_status: string | null;
    owners: string;
    team_names: string | null;
    team_count: number | null;
    oncall_user_names: string | null;
    unique_oncall_users: number | null;
    snr_current: number | null;
    snr_scope: string | null;
    snr_alert_count: number | null;
    snr_quick_resolved_alerts: number | null;
    snr_link: string | null;
    snr_last_updated: string | null;
    snr_last_week: number | null;
    snr_alert_count_last_week: number | null;
    snr_quick_resolved_last_week: number | null;
    snr_last_month: number | null;
    snr_alert_count_last_month: number | null;
    snr_quick_resolved_last_month: number | null;
}

const getSnrStatus = (
    snr: number | null,
    scope: string | null
): { label: string; color: string; bg: string } => {
    if (!scope) return { label: 'Unmonitored', color: '#757575', bg: '#f5f5f5' };
    if (snr === null) return { label: 'No Data', color: '#9e9e9e', bg: '#fafafa' };
    if (snr < 25) return { label: 'High Noise', color: '#c62828', bg: '#fff8f8' };
    if (snr < 50) return { label: 'Moderate', color: '#e65100', bg: '#fffaf5' };
    return { label: 'Good', color: '#2e7d32', bg: '#f6fff6' };
};

const getRecommendation = (
    snr: number | null,
    alertCount: number,
    quickResolved: number,
    scope: string | null,
    snrLastWeek: number | null,
    snrLastMonth: number | null
): string => {
    if (!scope) return 'Tag a Hedwig scope to start SNR monitoring for this application.';
    if (snr === null) return 'Scope configured but no alert data yet. Verify Hedwig scope is active.';

    // Build trend text (prefer week, fallback month)
    let trendText = '';
    if (snrLastWeek !== null) {
        const diff = snr - snrLastWeek;
        const arrow = diff > 0 ? '↑' : diff < 0 ? '↓' : '→';
        trendText = ` (${arrow} ${Math.abs(diff).toFixed(1)}% vs last week)`;
    } else if (snrLastMonth !== null) {
        const diff = snr - snrLastMonth;
        const arrow = diff > 0 ? '↑' : diff < 0 ? '↓' : '→';
        trendText = ` (${arrow} ${Math.abs(diff).toFixed(1)}% vs last month)`;
    }

    const noiseLevel = snr < 25 ? 'high' : snr < 50 ? 'moderate' : 'elevated';
    const noiseSuffix = quickResolved > 0 ? `, indicating ${noiseLevel} noise levels` : '';

    if (alertCount === 0) return `No alerts recorded. Scope is active — verify Hedwig is emitting data.`;

    const base = `${alertCount} alerts: ${quickResolved} resolved in <15 min${noiseSuffix}; SNR ≈ ${snr.toFixed(1)}%${trendText}`;

    if (snr < 25)
        return `${base} — Tune thresholds and suppress false positives.`;
    if (snr < 50)
        return `${base} — Consider suppressing repetitive low-value alerts.`;
    return `${base} — Signal quality is good. Continue monitoring.`;
};

const sortPriority = (a: AppRow): number => {
    if (!a.snr_scope) return 5;
    if (a.snr_current === null) return 4;
    if (a.snr_current < 25) return 1;
    if (a.snr_current < 50) return 2;
    return 3;
};

const SignalIntelligenceOverview: React.FC = () => {
    const { selectedPortfolio, setSelectedPortfolio, selectedPlatform, setSelectedPlatform } = useGlobalFilters();

    const [allApps, setAllApps] = useState<AppRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [portfolioOptions, setPortfolioOptions] = useState<string[]>([]);
    const [platformOptions, setPlatformOptions] = useState<string[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    // Keep a full unfiltered list just for populating filter dropdowns on first load
    const [allOptionsLoaded, setAllOptionsLoaded] = useState(false);

    // Load filter options once from unfiltered call
    useEffect(() => {
        if (allOptionsLoaded) return;
        authFetch(SNR.ALERT_INSIGHTS)
            .then((r) => r.json())
            .then((json) => {
                const data: AppRow[] = json.data || [];
                const portfolios = Array.from(new Set(data.map((a) => a.portfolio_name).filter(Boolean))).sort() as string[];
                const platforms = Array.from(new Set(data.map((a) => a.platform_name).filter(Boolean))).sort() as string[];
                setPortfolioOptions(portfolios);
                setPlatformOptions(platforms);
                setAllOptionsLoaded(true);
            })
            .catch(() => { /* non-critical */ });
    }, [allOptionsLoaded]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (selectedPortfolio && selectedPortfolio !== 'all') params.append('portfolio', selectedPortfolio);
            if (selectedPlatform && selectedPlatform !== 'all') params.append('platform', selectedPlatform);
            if (selectedCategory && selectedCategory !== 'all') params.append('category', selectedCategory);
            const url = `${SNR.ALERT_INSIGHTS}${params.toString() ? '?' + params.toString() : ''}`;
            const resp = await authFetch(url);
            if (!resp.ok) throw new Error(`API error ${resp.status}: ${resp.statusText}`);
            const json = await resp.json();
            if (!json.success) throw new Error(json.message || 'API returned failure');
            const parseNum = (v: any): number | null => {
                if (v === null || v === undefined || v === '') return null;
                const n = parseFloat(String(v));
                return isNaN(n) ? null : n;
            };
            const mapped: AppRow[] = (json.data || []).map((item: any) => ({
                ...item,
                snr_current: parseNum(item.snr_current),
                snr_alert_count: item.snr_alert_count !== null && item.snr_alert_count !== undefined ? parseInt(String(item.snr_alert_count)) : null,
                snr_quick_resolved_alerts: item.snr_quick_resolved_alerts !== null && item.snr_quick_resolved_alerts !== undefined ? parseInt(String(item.snr_quick_resolved_alerts)) : null,
                snr_last_week: parseNum(item.snr_last_week),
                snr_last_month: parseNum(item.snr_last_month),
                snr_alert_count_last_week: item.snr_alert_count_last_week !== null ? parseInt(String(item.snr_alert_count_last_week)) : null,
                snr_alert_count_last_month: item.snr_alert_count_last_month !== null ? parseInt(String(item.snr_alert_count_last_month)) : null,
                snr_quick_resolved_last_week: item.snr_quick_resolved_last_week !== null ? parseInt(String(item.snr_quick_resolved_last_week)) : null,
                snr_quick_resolved_last_month: item.snr_quick_resolved_last_month !== null ? parseInt(String(item.snr_quick_resolved_last_month)) : null,
                team_count: item.team_count !== null ? parseInt(String(item.team_count)) : null,
                unique_oncall_users: item.unique_oncall_users !== null ? parseInt(String(item.unique_oncall_users)) : null,
            }));
            setAllApps(mapped);
            setTotalCount(json.total_count ?? mapped.length);
        } catch (err: any) {
            setError(err.message || 'Unknown error');
        } finally {
            setLoading(false);
        }
    }, [selectedPortfolio, selectedPlatform, selectedCategory]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const sortedApps = [...allApps].sort((a, b) => {
        // Primary: descending alert count
        const aAlerts = a.snr_alert_count ?? 0;
        const bAlerts = b.snr_alert_count ?? 0;
        if (bAlerts !== aAlerts) return bAlerts - aAlerts;
        // Secondary: ascending SNR score (lower = noisier = higher priority)
        const aSnr = a.snr_current ?? 101;
        const bSnr = b.snr_current ?? 101;
        return aSnr - bSnr;
    });

    // Derived metrics - allApps is already filtered by backend based on category
    const withScope = allApps.filter((a) => a.snr_scope && String(a.snr_scope).trim());
    const noScope = allApps.filter((a) => !a.snr_scope || !String(a.snr_scope).trim());
    const withSnr = withScope.filter((a) => a.snr_current !== null);
    const highNoise = withSnr.filter((a) => (a.snr_current as number) < 25);
    const moderate = withSnr.filter((a) => (a.snr_current as number) >= 25 && (a.snr_current as number) < 50);
    const goodSignal = withSnr.filter((a) => (a.snr_current as number) >= 50);
    const avgSnr =
        withSnr.length > 0
            ? Math.round(withSnr.reduce((s, a) => s + (a.snr_current as number), 0) / withSnr.length)
            : null;
    const avgColor = avgSnr !== null ? (avgSnr >= 50 ? '#2e7d32' : avgSnr >= 25 ? '#e65100' : '#c62828') : '#9e9e9e';

    // Active flash card filter
    const [activeFilter, setActiveFilter] = useState<string | null>(null);

    // Column sort state — default: alerts desc
    const [sortCol, setSortCol] = useState<'alerts' | 'noise' | 'snr'>('alerts');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

    const handleSort = (col: 'alerts' | 'noise' | 'snr') => {
        if (sortCol === col) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortCol(col);
            setSortDir(col === 'snr' ? 'asc' : 'desc');
        }
    };

    const applySort = (apps: AppRow[]) => [...apps].sort((a, b) => {
        let aVal: number, bVal: number;
        if (sortCol === 'alerts') {
            aVal = a.snr_alert_count ?? 0;
            bVal = b.snr_alert_count ?? 0;
        } else if (sortCol === 'noise') {
            aVal = a.snr_quick_resolved_alerts ?? 0;
            bVal = b.snr_quick_resolved_alerts ?? 0;
        } else {
            aVal = a.snr_current ?? 101;
            bVal = b.snr_current ?? 101;
        }
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });

    const filteredApps = activeFilter === null ? allApps
        : activeFilter === 'monitored' ? allApps.filter(a => a.snr_scope && String(a.snr_scope).trim())
            : activeFilter === 'high' ? allApps.filter(a => a.snr_current !== null && a.snr_current < 25)
                : activeFilter === 'moderate' ? allApps.filter(a => a.snr_current !== null && a.snr_current >= 25 && a.snr_current < 50)
                    : activeFilter === 'good' ? allApps.filter(a => a.snr_current !== null && a.snr_current >= 50)
                        : activeFilter === 'unmonitored' ? allApps.filter(a => !a.snr_scope || !String(a.snr_scope).trim())
                            : allApps;

    const visibleApps = applySort(filteredApps);

    const flashCards = [
        { key: 'monitored', label: 'SNR Monitored', value: withScope.length, sub: `of ${allApps.length} apps`, color: '#1565c0', bg: '#fff', borderColor: '#1565c0' },
        { key: 'high', label: 'High Noise', value: highNoise.length, sub: 'SNR < 25% — Act Now', color: '#c62828', bg: '#ffebee', borderColor: '#c62828' },
        { key: 'moderate', label: 'Moderate Noise', value: moderate.length, sub: 'SNR 25–49% — Review', color: '#e65100', bg: '#fff3e0', borderColor: '#e65100' },
        { key: 'good', label: 'Good Signal', value: goodSignal.length, sub: 'SNR ≥ 50%', color: '#2e7d32', bg: '#e8f5e9', borderColor: '#2e7d32' },
        { key: null, label: 'Portfolio Avg SNR', value: avgSnr !== null ? `${avgSnr}%` : 'N/A', sub: `across ${withSnr.length} apps`, color: avgColor, bg: '#fff', borderColor: avgColor },
        { key: 'unmonitored', label: 'Not Monitored', value: noScope.length, sub: 'No Hedwig scope tagged', color: '#757575', bg: '#f5f5f5', borderColor: '#9e9e9e' },
    ];

    const rules = [
        { rule: '1', text: 'You build the platform. You fix your alerts.', sub: 'SRE is not the operator.' },
        { rule: '2', text: 'Use what already exists.', sub: "Extend Ops Excellence Portal — don't replace it." },
        { rule: '3', text: 'Only enterprise problems go to MTS.', sub: 'SNR is a platform-team concern.' },
    ];

    return (
        <Box sx={{ p: 3 }}>
            {/* Page Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1565c0', mb: 0.5 }}>
                        Signal Intelligence Overview
                    </Typography>

                </Box>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={fetchData}
                    disabled={loading}
                    sx={{ textTransform: 'none', fontSize: '0.78rem', borderColor: '#1565c0', color: '#1565c0' }}
                >
                    Refresh
                </Button>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Portfolio</InputLabel>
                    <Select
                        value={selectedPortfolio || 'all'}
                        label="Portfolio"
                        onChange={(e) => setSelectedPortfolio(e.target.value)}
                    >
                        <MenuItem value="all">All Portfolios</MenuItem>
                        {portfolioOptions.map((p) => (
                            <MenuItem key={p} value={p}>{p}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Platform</InputLabel>
                    <Select
                        value={selectedPlatform || 'all'}
                        label="Platform"
                        onChange={(e) => setSelectedPlatform(e.target.value)}
                    >
                        <MenuItem value="all">All Platforms</MenuItem>
                        {platformOptions.map((p) => (
                            <MenuItem key={p} value={p}>{p}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Category</InputLabel>
                    <Select
                        value={selectedCategory}
                        label="Category"
                        onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                        <MenuItem value="all">All Categories</MenuItem>
                        <MenuItem value="mission_critical">Mission Critical</MenuItem>
                        <MenuItem value="business_critical">Business Critical</MenuItem>
                        <MenuItem value="operational">Operational</MenuItem>
                    </Select>
                </FormControl>
                {!loading && allApps.length > 0 && (
                    <Typography variant="caption" sx={{ alignSelf: 'center', color: '#888', fontSize: '0.75rem' }}>
                        Showing {allApps.length}{totalCount > allApps.length ? ` of ${totalCount}` : ''} apps with Hedwig team configured
                    </Typography>
                )}
            </Box>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
            )}

            {!loading && !error && (
                <>
                    {/* Flash Cards */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                        {flashCards.map((card) => {
                            const isActive = activeFilter === card.key;
                            const isClickable = card.key !== null;
                            return (
                                <Box
                                    key={card.label}
                                    onClick={() => isClickable && setActiveFilter(isActive ? null : card.key)}
                                    sx={{
                                        minWidth: 130,
                                        p: 1.5,
                                        bgcolor: card.bg,
                                        border: isActive ? `2px solid ${card.borderColor}` : '1px solid #e0e0e0',
                                        borderLeft: `4px solid ${card.borderColor}`,
                                        borderRadius: 1,
                                        boxShadow: isActive ? 3 : 1,
                                        cursor: isClickable ? 'pointer' : 'default',
                                        transition: 'box-shadow 0.15s, border 0.15s',
                                        '&:hover': isClickable ? { boxShadow: 3, opacity: 0.92 } : {},
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#666', display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        {card.label}
                                    </Typography>
                                    <Typography sx={{ fontSize: '1.8rem', fontWeight: 700, color: card.color, lineHeight: 1 }}>
                                        {card.value}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.62rem', color: '#999' }}>
                                        {card.sub}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Platform Team Signal Board */}
                    <Paper elevation={1} sx={{ border: '1px solid #e0e0e0' }}>
                        <Box
                            sx={{
                                p: 1.5,
                                bgcolor: '#f5f7fa',
                                borderBottom: '1px solid #e0e0e0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 1,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1565c0' }}>
                                    Platform Team Signal Board
                                </Typography>
                                <Chip
                                    label={`${visibleApps.length} apps${activeFilter ? ` (filtered)` : ''}`}
                                    size="small"
                                    sx={{ bgcolor: activeFilter ? '#1565c0' : '#e3f2fd', color: activeFilter ? '#fff' : '#1565c0', fontSize: '0.65rem', height: 18 }}
                                />
                                {activeFilter && (
                                    <Chip
                                        label="Clear filter"
                                        size="small"
                                        onClick={() => setActiveFilter(null)}
                                        sx={{ fontSize: '0.62rem', height: 18, cursor: 'pointer', bgcolor: '#f5f5f5', color: '#555' }}
                                    />
                                )}
                            </Box>
                            <Typography variant="caption" sx={{ color: '#888', fontSize: '0.68rem', fontStyle: 'italic' }}>
                                Sorted by descending alerts, then SNR score
                            </Typography>
                        </Box>

                        <TableContainer sx={{ maxHeight: 'calc(100vh - 420px)' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ bgcolor: '#e3f2fd', fontWeight: 700, fontSize: '0.78rem', minWidth: 60 }}>App ID</TableCell>
                                        <TableCell sx={{ bgcolor: '#e3f2fd', fontWeight: 700, fontSize: '0.78rem', minWidth: 160 }}>Application</TableCell>
                                        <TableCell sx={{ bgcolor: '#e3f2fd', fontWeight: 700, fontSize: '0.78rem', minWidth: 130 }}>Platform</TableCell>
                                        <TableCell sx={{ bgcolor: '#e3f2fd', fontWeight: 700, fontSize: '0.78rem', minWidth: 130 }}>Portfolio</TableCell>
                                        <TableCell sx={{ bgcolor: '#e3f2fd', fontWeight: 700, fontSize: '0.78rem', minWidth: 150 }}>Owner(s)</TableCell>
                                        <TableCell sx={{ bgcolor: '#e3f2fd', fontWeight: 700, fontSize: '0.78rem', minWidth: 130 }}>
                                            <MuiTooltip title="On-call team(s) configured in Hedwig" arrow>
                                                <span>Hedwig Team(s)</span>
                                            </MuiTooltip>
                                        </TableCell>
                                        <TableCell sx={{ bgcolor: '#e3f2fd', fontWeight: 700, fontSize: '0.78rem', minWidth: 95 }} align="center">
                                            <TableSortLabel
                                                active={sortCol === 'snr'}
                                                direction={sortCol === 'snr' ? sortDir : 'asc'}
                                                onClick={() => handleSort('snr')}
                                                sx={{ '& .MuiTableSortLabel-icon': { fontSize: '0.7rem' } }}
                                            >
                                                SNR Score
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={{ bgcolor: '#e3f2fd', fontWeight: 700, fontSize: '0.78rem', minWidth: 75 }} align="center">
                                            <MuiTooltip title="Total alerts in last 30 days" arrow>
                                                <TableSortLabel
                                                    active={sortCol === 'alerts'}
                                                    direction={sortCol === 'alerts' ? sortDir : 'desc'}
                                                    onClick={() => handleSort('alerts')}
                                                    sx={{ '& .MuiTableSortLabel-icon': { fontSize: '0.7rem' } }}
                                                >
                                                    Alerts
                                                </TableSortLabel>
                                            </MuiTooltip>
                                        </TableCell>
                                        <TableCell sx={{ bgcolor: '#e3f2fd', fontWeight: 700, fontSize: '0.78rem', minWidth: 95 }} align="center">
                                            <MuiTooltip title="Alerts auto-resolved in < 15 min — potential noise" arrow>
                                                <TableSortLabel
                                                    active={sortCol === 'noise'}
                                                    direction={sortCol === 'noise' ? sortDir : 'desc'}
                                                    onClick={() => handleSort('noise')}
                                                    sx={{ '& .MuiTableSortLabel-icon': { fontSize: '0.7rem' } }}
                                                >
                                                    Noise Alerts
                                                </TableSortLabel>
                                            </MuiTooltip>
                                        </TableCell>
                                        <TableCell sx={{ bgcolor: '#e3f2fd', fontWeight: 700, fontSize: '0.78rem', minWidth: 140 }} align="center">Status</TableCell>
                                        <TableCell sx={{ bgcolor: '#e3f2fd', fontWeight: 700, fontSize: '0.78rem', minWidth: 300 }}>Recommended Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {visibleApps.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={11} align="center" sx={{ py: 5, color: '#888', fontStyle: 'italic' }}>
                                                No apps found for the selected filter.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        visibleApps.map((app, idx) => {
                                            const snr = app.snr_current;
                                            const alertCount = app.snr_alert_count ?? 0;
                                            const quickResolved = app.snr_quick_resolved_alerts ?? 0;
                                            const status = getSnrStatus(snr, app.snr_scope);
                                            const recommendation = getRecommendation(snr, alertCount, quickResolved, app.snr_scope, app.snr_last_week, app.snr_last_month);

                                            const renderTrend = (current: number | null, prev: number | null) => {
                                                if (current === null || prev === null)
                                                    return <Typography variant="caption" sx={{ color: '#bbb', fontSize: '0.75rem' }}>—</Typography>;
                                                const diff = current - prev;
                                                const tColor = diff > 0 ? '#2e7d32' : diff < 0 ? '#c62828' : '#757575';
                                                const arrow = diff > 0 ? '↑' : diff < 0 ? '↓' : '→';
                                                return (
                                                    <Typography variant="caption" sx={{ color: tColor, fontWeight: 600, fontSize: '0.75rem' }}>
                                                        {arrow} {Math.abs(diff).toFixed(1)}%
                                                    </Typography>
                                                );
                                            };

                                            const rowBg = idx % 2 === 0 ? status.bg : undefined;

                                            return (
                                                <TableRow
                                                    key={`sio-${app.app_id}-${idx}`}
                                                    sx={{ bgcolor: rowBg, '&:hover': { filter: 'brightness(0.97)' } }}
                                                >
                                                    <TableCell sx={{ fontSize: '0.7rem', color: '#888' }}>{app.app_id}</TableCell>
                                                    <TableCell sx={{ fontSize: '0.75rem' }}>
                                                        {app.snr_link ? (
                                                            <a
                                                                href={app.snr_link}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ color: '#1565c0', textDecoration: 'none', fontWeight: 600 }}
                                                                onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                                                                onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                                                            >
                                                                {app.app_name}
                                                            </a>
                                                        ) : (
                                                            <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{app.app_name}</Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{app.platform_name || '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: '0.73rem', color: '#555' }}>{app.portfolio_name || '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: '0.72rem', color: '#555', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        <MuiTooltip title={app.owners || ''} arrow placement="top">
                                                            <span>{app.owners || '—'}</span>
                                                        </MuiTooltip>
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.72rem', color: '#444' }}>
                                                        {app.team_names ? (
                                                            <MuiTooltip title={`On-call: ${app.oncall_user_names || 'N/A'}`} arrow placement="top">
                                                                <span>{app.team_names}</span>
                                                            </MuiTooltip>
                                                        ) : (
                                                            <Typography variant="caption" sx={{ color: '#bbb', fontSize: '0.72rem' }}>—</Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {snr !== null ? (
                                                            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: status.color }}>
                                                                {snr.toFixed(1)}%
                                                            </Typography>
                                                        ) : (
                                                            <Typography variant="caption" sx={{ color: '#bbb', fontSize: '0.75rem' }}>N/A</Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.78rem' }}>
                                                            {alertCount > 0 ? alertCount : '—'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {quickResolved > 0 ? (
                                                            <MuiTooltip title="Auto-resolved in < 15 min — likely noise" arrow>
                                                                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#e65100' }}>
                                                                    {quickResolved}
                                                                </Typography>
                                                            </MuiTooltip>
                                                        ) : (
                                                            <Typography variant="caption" sx={{ color: '#bbb', fontSize: '0.78rem' }}>—</Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={status.label}
                                                            size="small"
                                                            sx={{ bgcolor: status.color, color: '#fff', fontWeight: 700, fontSize: '0.65rem', height: 20, mb: 0.5 }}
                                                        />
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2, mt: 0.3 }}>
                                                            {renderTrend(snr, app.snr_last_week) !== null && (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                                                    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#999' }}>1W:</Typography>
                                                                    {renderTrend(snr, app.snr_last_week)}
                                                                </Box>
                                                            )}
                                                            {renderTrend(snr, app.snr_last_month) !== null && (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                                                    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#999' }}>1M:</Typography>
                                                                    {renderTrend(snr, app.snr_last_month)}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.73rem', color: '#444', maxWidth: 320, whiteSpace: 'normal' }}>
                                                        {(() => {
                                                            const fmtNum = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n);
                                                            const selfRecovPct = alertCount > 0 ? Math.round((quickResolved / alertCount) * 100) : 0;
                                                            return (
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
                                                                    {/* Scope chip */}
                                                                    {app.snr_scope ? (
                                                                        <Chip
                                                                            label={`Scope: ${app.snr_scope}`}
                                                                            size="small"
                                                                            sx={{
                                                                                bgcolor: status.color + '18',
                                                                                color: status.color,
                                                                                border: `1px solid ${status.color}55`,
                                                                                fontWeight: 600,
                                                                                fontSize: '0.62rem',
                                                                                height: 18,
                                                                                alignSelf: 'flex-start',
                                                                                '& .MuiChip-label': { px: 0.8 },
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <Chip
                                                                            label="No Scope Configured"
                                                                            size="small"
                                                                            sx={{
                                                                                bgcolor: '#f5f5f5',
                                                                                color: '#757575',
                                                                                border: '1px dashed #bbb',
                                                                                fontWeight: 600,
                                                                                fontSize: '0.62rem',
                                                                                height: 18,
                                                                                alignSelf: 'flex-start',
                                                                                '& .MuiChip-label': { px: 0.8 },
                                                                            }}
                                                                        />
                                                                    )}
                                                                    {/* Alert Effectiveness stats */}
                                                                    {(alertCount > 0 || app.snr_scope) && (
                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1565c0', minWidth: 42 }}>{alertCount > 0 ? fmtNum(alertCount) : '—'}</Typography>
                                                                                <Typography sx={{ fontSize: '0.7rem', color: '#555' }}>Alerts Generated</Typography>
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#e65100', minWidth: 42 }}>
                                                                                    {quickResolved > 0 ? `${fmtNum(quickResolved)} (${selfRecovPct}%)` : '—'}
                                                                                </Typography>
                                                                                <Typography sx={{ fontSize: '0.7rem', color: '#555' }}>Self-Recovered &lt;15 min</Typography>
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: status.color, minWidth: 42 }}>
                                                                                    {snr !== null ? `${snr.toFixed(1)}%` : '—'}
                                                                                </Typography>
                                                                                <Typography sx={{ fontSize: '0.7rem', color: '#555' }}>Actionable Signal Rate</Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    )}
                                                                    {/* Link to Hedwig */}
                                                                    {app.snr_link && (
                                                                        <a
                                                                            href={app.snr_link}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            style={{ color: '#1565c0', fontSize: '0.7rem', fontWeight: 600, textDecoration: 'none' }}
                                                                            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                                                                            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                                                                        >
                                                                            View in Hedwig →
                                                                        </a>
                                                                    )}
                                                                </Box>
                                                            );
                                                        })()}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </>
            )}
        </Box>
    );
};

export default SignalIntelligenceOverview;
